/**
 * Phase 119.14 - Lazy-loaded non-Latin Google Fonts.
 *
 * Why this exists
 * ───────────────
 * Lighthouse on the production landing page flagged ~2.66 s of render-
 * blocking time on Google Font CSS files - the 11 non-Latin scripts
 * (Devanagari, Bengali, Tamil, Telugu, Gujarati, Kannada, Malayalam,
 * Gurmukhi, Urdu, Simplified-Chinese, Japanese) were all `@import`-ed
 * synchronously from `index.css`, shipping ~240 KB of CSS to every
 * visitor on first paint - even though 99 % of visitors only ever read
 * Latin script.
 *
 * The fix
 * ───────
 * Strip every non-Latin `@import` from `index.css`, then inject a
 * `<link rel="stylesheet">` for the matching CSS only when i18n
 * actually switches to that locale (or on first render if the user's
 * detected language belongs to one of those scripts). Tags are idempotent
 * by `data-byb-font-locale` so repeat calls never duplicate.
 *
 * NB: we deliberately use `<link>` (not `@import`) because `<link>` is
 * non-blocking - it doesn't stall the next CSS parse.
 */

const FONT_URL_BY_LOCALE = {
  // Devanagari (Hindi, Marathi)
  hi: 'https://fonts.googleapis.com/css2?family=Baloo+2:wght@400;500;600;700&family=Noto+Sans+Devanagari:wght@400;500;600;700&display=swap',
  mr: 'https://fonts.googleapis.com/css2?family=Baloo+2:wght@400;500;600;700&family=Noto+Sans+Devanagari:wght@400;500;600;700&display=swap',
  // Bengali
  bn: 'https://fonts.googleapis.com/css2?family=Baloo+Da+2:wght@400;500;600;700&family=Noto+Sans+Bengali:wght@400;500;600;700&display=swap',
  // Tamil
  ta: 'https://fonts.googleapis.com/css2?family=Baloo+Thambi+2:wght@400;500;600;700&family=Noto+Sans+Tamil:wght@400;500;600;700&display=swap',
  // Telugu
  te: 'https://fonts.googleapis.com/css2?family=Baloo+Tammudu+2:wght@400;500;600;700&family=Noto+Sans+Telugu:wght@400;500;600;700&display=swap',
  // Gujarati
  gu: 'https://fonts.googleapis.com/css2?family=Baloo+Bhai+2:wght@400;500;600;700&family=Noto+Sans+Gujarati:wght@400;500;600;700&display=swap',
  // Kannada
  kn: 'https://fonts.googleapis.com/css2?family=Baloo+Tamma+2:wght@400;500;600;700&family=Noto+Sans+Kannada:wght@400;500;600;700&display=swap',
  // Malayalam
  ml: 'https://fonts.googleapis.com/css2?family=Baloo+Chettan+2:wght@400;500;600;700&family=Noto+Sans+Malayalam:wght@400;500;600;700&display=swap',
  // Gurmukhi (Punjabi)
  pa: 'https://fonts.googleapis.com/css2?family=Baloo+Paaji+2:wght@400;500;600;700&family=Noto+Sans+Gurmukhi:wght@400;500;600;700&display=swap',
  // Urdu
  ur: 'https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu:wght@400;500;600;700&display=swap',
  // Simplified Chinese
  zh: 'https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;500;600;700&display=swap',
  'zh-CN': 'https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;500;600;700&display=swap',
  // Japanese
  ja: 'https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;600;700&display=swap',
};

/**
 * Inject the font CSS for `locale` exactly once. No-op for Latin locales.
 * Safe to call on every i18n `languagechanged` event.
 */
export function loadFontForLocale(locale) {
  if (!locale || typeof document === 'undefined') return;
  // Try the full locale first (e.g. "zh-CN"), then the base ("zh").
  const base = String(locale).split('-')[0].toLowerCase();
  const url = FONT_URL_BY_LOCALE[locale] || FONT_URL_BY_LOCALE[base];
  if (!url) return;
  // Idempotent - bail if the locale was already injected.
  const marker = `data-byb-font-locale-${base}`;
  if (document.querySelector(`link[${marker}]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  link.setAttribute(marker, '1');
  // Hint the browser to use a passive priority - this CSS only impacts
  // glyph rendering for the active locale, never first paint.
  link.media = 'print';
  link.onload = () => { link.media = 'all'; };
  document.head.appendChild(link);
}

export default loadFontForLocale;
